// =============================================================================
// Country data for the Trasferte map
// =============================================================================
//
// Names are NOT stored here. They come from Intl.DisplayNames, which every
// browser ships with the full CLDR list in every locale — so "CM" is Cameroon
// in English and Camerun in Italian without either being typed out, and the
// same is true of the other 247.
//
// The previous version kept a hand-written table of 66 English names. The map
// draws 248 countries, so the other 182 fell through to the fallback and were
// labelled with their bare code: hovering Cameroon showed "CM". Search had the
// same ceiling, since it iterated that table — which is also why it worked in
// one language and not the other.
//
// What is stored here is what Intl cannot derive: which currency a country
// uses, the numeric codes the map file uses as ids, and the alpha-3 codes
// viaggiaresicuri.it puts in its URLs.
// =============================================================================

// CLDR distinguishes the two Congos by capital city rather than by official
// name, so the map labelled them "Congo - Kinshasa" and "Congo-Brazzaville".
// Correct, and not what anybody calls them.
//
// All 248 names were dumped from CLDR and read: these two are the only ones
// worth overriding. The other awkward-looking results — Guinea-Bissau, Timor
// Est, Saint-Barthélemy, Isole Cocos (Keeling), Myanmar (Birmania) — are the
// real names of those places and are left alone.
const NAME_OVERRIDES = {
  it: {
    CD: "Repubblica Democratica del Congo",
    CG: "Repubblica del Congo",
  },
  en: {
    CD: "Democratic Republic of the Congo",
    CG: "Republic of the Congo",
  },
};

// ISO 3166-1 alpha-2 → ISO 4217 currency. Covers all 248 countries the map
// draws. Antarctica is the one entry with no currency, which is correct.
export const COUNTRY_CURRENCY = {
  AD: "EUR", AE: "AED", AF: "AFN", AG: "XCD", AI: "XCD", AL: "ALL", AM: "AMD",
  AO: "AOA", AQ: null, AR: "ARS", AS: "USD", AT: "EUR", AU: "AUD", AW: "AWG",
  AX: "EUR", AZ: "AZN", BA: "BAM", BB: "BBD", BD: "BDT", BE: "EUR", BF: "XOF",
  BG: "BGN", BH: "BHD", BI: "BIF", BJ: "XOF", BL: "EUR", BM: "BMD", BN: "BND",
  BO: "BOB", BQ: "USD", BR: "BRL", BS: "BSD", BT: "BTN", BW: "BWP", BY: "BYN",
  BZ: "BZD", CA: "CAD", CC: "AUD", CD: "CDF", CF: "XAF", CG: "XAF", CH: "CHF",
  CI: "XOF", CK: "NZD", CL: "CLP", CM: "XAF", CN: "CNY", CO: "COP", CR: "CRC",
  CU: "CUP", CV: "CVE", CW: "ANG", CX: "AUD", CY: "EUR", CZ: "CZK", DE: "EUR",
  DJ: "DJF", DK: "DKK", DM: "XCD", DO: "DOP", DZ: "DZD", EC: "USD", EE: "EUR",
  EG: "EGP", EH: "MAD", ER: "ERN", ES: "EUR", ET: "ETB", FI: "EUR", FJ: "FJD",
  FK: "FKP", FM: "USD", FO: "DKK", FR: "EUR", GA: "XAF", GB: "GBP", GD: "XCD",
  GE: "GEL", GF: "EUR", GG: "GBP", GH: "GHS", GI: "GIP", GL: "DKK", GM: "GMD",
  GN: "GNF", GP: "EUR", GQ: "XAF", GR: "EUR", GS: "GBP", GT: "GTQ", GU: "USD",
  GW: "XOF", GY: "GYD", HK: "HKD", HM: "AUD", HN: "HNL", HR: "EUR", HT: "HTG",
  HU: "HUF", ID: "IDR", IE: "EUR", IL: "ILS", IM: "GBP", IN: "INR", IO: "USD",
  IQ: "IQD", IR: "IRR", IS: "ISK", IT: "EUR", JE: "GBP", JM: "JMD", JO: "JOD",
  JP: "JPY", KE: "KES", KG: "KGS", KH: "KHR", KI: "AUD", KM: "KMF", KN: "XCD",
  KP: "KPW", KR: "KRW", KW: "KWD", KY: "KYD", KZ: "KZT", LA: "LAK", LB: "LBP",
  LC: "XCD", LI: "CHF", LK: "LKR", LR: "LRD", LS: "LSL", LT: "EUR", LU: "EUR",
  LV: "EUR", LY: "LYD", MA: "MAD", MC: "EUR", MD: "MDL", ME: "EUR", MF: "EUR",
  MG: "MGA", MH: "USD", MK: "MKD", ML: "XOF", MM: "MMK", MN: "MNT", MO: "MOP",
  MP: "USD", MQ: "EUR", MR: "MRU", MS: "XCD", MT: "EUR", MU: "MUR", MV: "MVR",
  MW: "MWK", MX: "MXN", MY: "MYR", MZ: "MZN", NA: "NAD", NC: "XPF", NE: "XOF",
  NF: "AUD", NG: "NGN", NI: "NIO", NL: "EUR", NO: "NOK", NP: "NPR", NR: "AUD",
  NU: "NZD", NZ: "NZD", OM: "OMR", PA: "PAB", PE: "PEN", PF: "XPF", PG: "PGK",
  PH: "PHP", PK: "PKR", PL: "PLN", PM: "EUR", PN: "NZD", PR: "USD", PS: "ILS",
  PT: "EUR", PW: "USD", PY: "PYG", QA: "QAR", RE: "EUR", RO: "RON", RS: "RSD",
  RU: "RUB", RW: "RWF", SA: "SAR", SB: "SBD", SC: "SCR", SD: "SDG", SE: "SEK",
  SG: "SGD", SH: "SHP", SI: "EUR", SJ: "NOK", SK: "EUR", SL: "SLE", SM: "EUR",
  SN: "XOF", SO: "SOS", SR: "SRD", SS: "SSP", ST: "STN", SV: "USD", SX: "ANG",
  SY: "SYP", SZ: "SZL", TC: "USD", TD: "XAF", TF: "EUR", TG: "XOF", TH: "THB",
  TJ: "TJS", TK: "NZD", TL: "USD", TM: "TMT", TN: "TND", TO: "TOP", TR: "TRY",
  TT: "TTD", TV: "AUD", TW: "TWD", TZ: "TZS", UA: "UAH", UG: "UGX", UM: "USD",
  US: "USD", UY: "UYU", UZ: "UZS", VA: "EUR", VC: "XCD", VE: "VES", VG: "USD",
  VI: "USD", VN: "VND", VU: "VUV", WF: "XPF", WS: "WST", YE: "YER", YT: "EUR",
  ZA: "ZAR", ZM: "ZMW", ZW: "ZWG",
};

// Mapping from world-atlas TopoJSON numeric ISO codes to ISO Alpha-2.
// world-atlas exposes `id` as the 3-digit ISO 3166-1 numeric code.
export const ISO_NUMERIC_TO_ALPHA2 = {
  "004": "AF", "008": "AL", "010": "AQ", "012": "DZ", "016": "AS", "020": "AD",
  "024": "AO", "028": "AG", "031": "AZ", "032": "AR", "036": "AU", "040": "AT",
  "044": "BS", "048": "BH", "050": "BD", "051": "AM", "052": "BB", "056": "BE",
  "060": "BM", "064": "BT", "068": "BO", "070": "BA", "072": "BW", "076": "BR",
  "084": "BZ", "086": "IO", "090": "SB", "092": "VG", "096": "BN", "100": "BG",
  "104": "MM", "108": "BI", "112": "BY", "116": "KH", "120": "CM", "124": "CA",
  "132": "CV", "136": "KY", "140": "CF", "144": "LK", "148": "TD", "152": "CL",
  "156": "CN", "158": "TW", "162": "CX", "166": "CC", "170": "CO", "174": "KM",
  "175": "YT", "178": "CG", "180": "CD", "184": "CK", "188": "CR", "191": "HR",
  "192": "CU", "196": "CY", "203": "CZ", "204": "BJ", "208": "DK", "212": "DM",
  "214": "DO", "218": "EC", "222": "SV", "226": "GQ", "231": "ET", "232": "ER",
  "233": "EE", "234": "FO", "238": "FK", "239": "GS", "242": "FJ", "246": "FI",
  "248": "AX", "250": "FR", "254": "GF", "258": "PF", "260": "TF", "262": "DJ",
  "266": "GA", "268": "GE", "270": "GM", "275": "PS", "276": "DE", "288": "GH",
  "292": "GI", "296": "KI", "300": "GR", "304": "GL", "308": "GD", "312": "GP",
  "316": "GU", "320": "GT", "324": "GN", "328": "GY", "332": "HT", "334": "HM",
  "336": "VA", "340": "HN", "344": "HK", "348": "HU", "352": "IS", "356": "IN",
  "360": "ID", "364": "IR", "368": "IQ", "372": "IE", "376": "IL", "380": "IT",
  "384": "CI", "388": "JM", "392": "JP", "398": "KZ", "400": "JO", "404": "KE",
  "408": "KP", "410": "KR", "414": "KW", "417": "KG", "418": "LA", "422": "LB",
  "426": "LS", "428": "LV", "430": "LR", "434": "LY", "438": "LI", "440": "LT",
  "442": "LU", "446": "MO", "450": "MG", "454": "MW", "458": "MY", "462": "MV",
  "466": "ML", "470": "MT", "474": "MQ", "478": "MR", "480": "MU", "484": "MX",
  "492": "MC", "496": "MN", "498": "MD", "499": "ME", "500": "MS", "504": "MA",
  "508": "MZ", "512": "OM", "516": "NA", "520": "NR", "524": "NP", "528": "NL",
  "531": "CW", "533": "AW", "534": "SX", "535": "BQ", "540": "NC", "548": "VU",
  "554": "NZ", "558": "NI", "562": "NE", "566": "NG", "570": "NU", "574": "NF",
  "578": "NO", "580": "MP", "581": "UM", "583": "FM", "584": "MH", "585": "PW",
  "586": "PK", "591": "PA", "598": "PG", "600": "PY", "604": "PE", "608": "PH",
  "612": "PN", "616": "PL", "620": "PT", "624": "GW", "626": "TL", "630": "PR",
  "634": "QA", "638": "RE", "642": "RO", "643": "RU", "646": "RW", "652": "BL",
  "654": "SH", "659": "KN", "660": "AI", "662": "LC", "663": "MF", "666": "PM",
  "670": "VC", "674": "SM", "678": "ST", "682": "SA", "686": "SN", "688": "RS",
  "690": "SC", "694": "SL", "702": "SG", "703": "SK", "704": "VN", "705": "SI",
  "706": "SO", "710": "ZA", "716": "ZW", "724": "ES", "728": "SS", "729": "SD",
  "732": "EH", "740": "SR", "744": "SJ", "748": "SZ", "752": "SE", "756": "CH",
  "760": "SY", "762": "TJ", "764": "TH", "768": "TG", "772": "TK", "776": "TO",
  "780": "TT", "784": "AE", "788": "TN", "792": "TR", "795": "TM", "796": "TC",
  "798": "TV", "800": "UG", "804": "UA", "807": "MK", "818": "EG", "826": "GB",
  "831": "GG", "832": "JE", "833": "IM", "834": "TZ", "840": "US", "850": "VI",
  "854": "BF", "858": "UY", "860": "UZ", "862": "VE", "876": "WF", "882": "WS",
  "887": "YE", "894": "ZM",
};

// Every alpha-2 the map can produce, sorted, so callers do not have to derive
// the list from the numeric mapping.
export const ALL_COUNTRY_CODES = Object.values(ISO_NUMERIC_TO_ALPHA2).sort();

// ---------------------------------------------------------------------------
// Names, via the browser's own CLDR data
// ---------------------------------------------------------------------------

// Intl.DisplayNames instances are not cheap to build, and the search runs over
// 248 codes on every keystroke, so they are built once per locale.
const _regionCache = {};
const _currencyCache = {};

function regionNames(locale) {
  if (!_regionCache[locale]) {
    try {
      _regionCache[locale] = new Intl.DisplayNames([locale], { type: "region" });
    } catch {
      _regionCache[locale] = null;
    }
  }
  return _regionCache[locale];
}

function currencyNames(locale) {
  if (!_currencyCache[locale]) {
    try {
      _currencyCache[locale] = new Intl.DisplayNames([locale], { type: "currency" });
    } catch {
      _currencyCache[locale] = null;
    }
  }
  return _currencyCache[locale];
}

/**
 * Country name in the given language. Falls back to the code, which is what
 * the whole map used to do for 182 of its 248 countries.
 */
export function countryName(code, language = "it") {
  if (!code) return "";
  const upper = code.toUpperCase();

  const override = (NAME_OVERRIDES[language] || NAME_OVERRIDES.en)[upper];
  if (override) return override;

  const dn = regionNames(language);
  if (!dn) return upper;
  try {
    const name = dn.of(upper);
    return name && name !== upper ? name : upper;
  } catch {
    return upper;
  }
}

/** Currency code for a country, or null where there is none. */
export function getCurrencyCode(code) {
  return COUNTRY_CURRENCY[code] || null;
}

/**
 * Currency for a country: its ISO code and its name in the given language,
 * or null when the country has no currency of its own.
 */
export function getCurrencyInfo(code, language = "it") {
  const currency = COUNTRY_CURRENCY[code];
  if (!currency) return null;
  const dn = currencyNames(language);
  let name = currency;
  if (dn) {
    try {
      const resolved = dn.of(currency);
      if (resolved) name = resolved;
    } catch {
      /* keep the code as the name */
    }
  }
  return { currency_code: currency, currency_name: name };
}

/**
 * Search every country by name or code.
 *
 * Matches the name in the current language AND in English, so "Germany" finds
 * Germania while the interface is in Italian and "Camerun" finds Cameroon
 * while it is in English. Names that start with the query sort first, since
 * typing "ind" should offer India before Indonesia.
 */
export function searchCountries(query, language = "it", limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];

  const results = [];
  for (const code of ALL_COUNTRY_CODES) {
    const local = countryName(code, language);
    const english = language === "en" ? local : countryName(code, "en");
    const lowLocal = local.toLowerCase();
    const lowEnglish = english.toLowerCase();

    if (lowLocal.includes(q) || lowEnglish.includes(q) || code.toLowerCase() === q) {
      results.push({
        code,
        name: local,
        rank: lowLocal.startsWith(q) || lowEnglish.startsWith(q) ? 0 : 1,
      });
    }
  }

  results.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name, language));
  return results.slice(0, limit);
}

// ISO 3166-1 Alpha-2 → Alpha-3, used for viaggiaresicuri.it URLs. Verified
// against the numeric mapping above: every country the map can produce has an
// entry here, and no two share an alpha-3.
export const ALPHA2_TO_ALPHA3 = {
  AD: "AND", AE: "ARE", AF: "AFG", AG: "ATG", AI: "AIA", AL: "ALB", AM: "ARM",
  AO: "AGO", AQ: "ATA", AR: "ARG", AS: "ASM", AT: "AUT", AU: "AUS", AW: "ABW",
  AX: "ALA", AZ: "AZE", BA: "BIH", BB: "BRB", BD: "BGD", BE: "BEL", BF: "BFA",
  BG: "BGR", BH: "BHR", BI: "BDI", BJ: "BEN", BL: "BLM", BM: "BMU", BN: "BRN",
  BO: "BOL", BQ: "BES", BR: "BRA", BS: "BHS", BT: "BTN", BV: "BVT", BW: "BWA",
  BY: "BLR", BZ: "BLZ", CA: "CAN", CC: "CCK", CD: "COD", CF: "CAF", CG: "COG",
  CH: "CHE", CI: "CIV", CK: "COK", CL: "CHL", CM: "CMR", CN: "CHN", CO: "COL",
  CR: "CRI", CU: "CUB", CV: "CPV", CW: "CUW", CX: "CXR", CY: "CYP", CZ: "CZE",
  DE: "DEU", DJ: "DJI", DK: "DNK", DM: "DMA", DO: "DOM", DZ: "DZA", EC: "ECU",
  EE: "EST", EG: "EGY", EH: "ESH", ER: "ERI", ES: "ESP", ET: "ETH", FI: "FIN",
  FJ: "FJI", FK: "FLK", FM: "FSM", FO: "FRO", FR: "FRA", GA: "GAB", GB: "GBR",
  GD: "GRD", GE: "GEO", GF: "GUF", GG: "GGY", GH: "GHA", GI: "GIB", GL: "GRL",
  GM: "GMB", GN: "GIN", GP: "GLP", GQ: "GNQ", GR: "GRC", GS: "SGS", GT: "GTM",
  GU: "GUM", GW: "GNB", GY: "GUY", HK: "HKG", HM: "HMD", HN: "HND", HR: "HRV",
  HT: "HTI", HU: "HUN", ID: "IDN", IE: "IRL", IL: "ISR", IM: "IMN", IN: "IND",
  IO: "IOT", IQ: "IRQ", IR: "IRN", IS: "ISL", IT: "ITA", JE: "JEY", JM: "JAM",
  JO: "JOR", JP: "JPN", KE: "KEN", KG: "KGZ", KH: "KHM", KI: "KIR", KM: "COM",
  KN: "KNA", KP: "PRK", KR: "KOR", KW: "KWT", KY: "CYM", KZ: "KAZ", LA: "LAO",
  LB: "LBN", LC: "LCA", LI: "LIE", LK: "LKA", LR: "LBR", LS: "LSO", LT: "LTU",
  LU: "LUX", LV: "LVA", LY: "LBY", MA: "MAR", MC: "MCO", MD: "MDA", ME: "MNE",
  MF: "MAF", MG: "MDG", MH: "MHL", MK: "MKD", ML: "MLI", MM: "MMR", MN: "MNG",
  MO: "MAC", MP: "MNP", MQ: "MTQ", MR: "MRT", MS: "MSR", MT: "MLT", MU: "MUS",
  MV: "MDV", MW: "MWI", MX: "MEX", MY: "MYS", MZ: "MOZ", NA: "NAM", NC: "NCL",
  NE: "NER", NF: "NFK", NG: "NGA", NI: "NIC", NL: "NLD", NO: "NOR", NP: "NPL",
  NR: "NRU", NU: "NIU", NZ: "NZL", OM: "OMN", PA: "PAN", PE: "PER", PF: "PYF",
  PG: "PNG", PH: "PHL", PK: "PAK", PL: "POL", PM: "SPM", PN: "PCN", PR: "PRI",
  PS: "PSE", PT: "PRT", PW: "PLW", PY: "PRY", QA: "QAT", RE: "REU", RO: "ROU",
  RS: "SRB", RU: "RUS", RW: "RWA", SA: "SAU", SB: "SLB", SC: "SYC", SD: "SDN",
  SE: "SWE", SG: "SGP", SH: "SHN", SI: "SVN", SJ: "SJM", SK: "SVK", SL: "SLE",
  SM: "SMR", SN: "SEN", SO: "SOM", SR: "SUR", SS: "SSD", ST: "STP", SV: "SLV",
  SX: "SXM", SY: "SYR", SZ: "SWZ", TC: "TCA", TD: "TCD", TF: "ATF", TG: "TGO",
  TH: "THA", TJ: "TJK", TK: "TKL", TL: "TLS", TM: "TKM", TN: "TUN", TO: "TON",
  TR: "TUR", TT: "TTO", TV: "TUV", TW: "TWN", TZ: "TZA", UA: "UKR", UG: "UGA",
  UM: "UMI", US: "USA", UY: "URY", UZ: "UZB", VA: "VAT", VC: "VCT", VE: "VEN",
  VG: "VGB", VI: "VIR", VN: "VNM", VU: "VUT", WF: "WLF", WS: "WSM", YE: "YEM",
  YT: "MYT", ZA: "ZAF", ZM: "ZMB", ZW: "ZWE",
};

export function getAlpha3(alpha2) {
  return ALPHA2_TO_ALPHA3[alpha2] || alpha2;
}

/** The viaggiaresicuri.it country page, which is keyed by alpha-3. */
export function viaggiareSicuriUrl(alpha2) {
  return `https://www.viaggiaresicuri.it/find-country/country/${getAlpha3(alpha2)}`;
}

// Kept so existing callers do not break; both take the language rather than
// the translation function, since a country name is not UI copy.
export const localizedCountryName = countryName;
export function localizedCurrencyName(currencyCode, language = "it") {
  const dn = currencyNames(language);
  if (!dn) return currencyCode;
  try {
    return dn.of(currencyCode) || currencyCode;
  } catch {
    return currencyCode;
  }
}
